import { useCallback, useRef } from "react";

/**
 * The Cloudflare Turnstile widget (`server/turnstile.js` verifies its token before proxying).
 *
 * Rendered explicitly, not by the script's `cf-turnstile` class scan: that scan runs once at page
 * load, and this SPA mounts its forms later, on navigation. Await `getToken()` on submit (it
 * waits for a check still running), then call `reset()`: a token is single-use.
 */
type TurnstileApi = {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

/** Hides the widget (`.env.local`, or deploy.yml on Test). Only the server's TURNSTILE_DISABLED turns the check off. */
const DISABLED = import.meta.env.VITE_TURNSTILE_DISABLED === "true";
const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let script: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
  script ??= new Promise((resolve, reject) => {
    const tag = document.createElement("script");
    tag.src = SCRIPT;
    tag.async = true;
    tag.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile did not load")));
    tag.onerror = () => {
      script = undefined; // let the next form try again
      reject(new Error("Turnstile did not load"));
    };
    document.head.appendChild(tag);
  });
  return script;
}

export function useTurnstile(action: string) {
  const widgetId = useRef<string | undefined>(undefined);
  const token = useRef("");
  const waiting = useRef<((token: string) => void)[]>([]);

  // A callback ref, not an effect: a form that steps away and back mounts a new container, and
  // each one needs its own widget.
  const container = useCallback(
    (element: HTMLDivElement) => {
      let gone = false;
      const settle = (next: string) => {
        token.current = next;
        waiting.current.splice(0).forEach((resolve) => resolve(next));
      };
      loadTurnstile()
        .then((turnstile) => {
          if (gone) return;
          widgetId.current = turnstile.render(element, {
            sitekey: SITE_KEY,
            action,
            // Always shown, as wide as the form's fields, light to match the page.
            size: "flexible",
            theme: "light",
            callback: settle,
            "expired-callback": () => (token.current = ""),
            "error-callback": () => settle(""),
          });
        })
        .catch(() => {}); // no widget, no token: the server answers "Complete the security check."
      return () => {
        gone = true;
        if (widgetId.current) window.turnstile?.remove(widgetId.current);
        widgetId.current = undefined;
        settle("");
      };
    },
    [action],
  );

  /** The token, waiting up to 15 s while the check still runs; "" when there is no widget or it failed. */
  function getToken(): Promise<string> {
    if (token.current || !widgetId.current) return Promise.resolve(token.current);
    return new Promise((resolve) => {
      waiting.current.push(resolve);
      setTimeout(() => resolve(""), 15_000);
    });
  }

  /** A token is single-use: call after every request so the next attempt gets a fresh one. */
  function reset() {
    token.current = "";
    if (widgetId.current) window.turnstile?.reset(widgetId.current);
  }

  // Disabled: no widget, so getToken() answers "" at once and the dev server waves it through.
  return { widget: DISABLED ? null : <div ref={container} />, getToken, reset };
}
